import { InlineCtaCard } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import {
  BoosterPlan,
  TargetTest,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { Box, SxProps, Theme } from '@mui/material';

import { InsufficientScheduleCallout } from './components/InsufficientScheduleCallout';
import { PlanAtAGlance } from './components/PlanAtAGlance';
import { ProjectionCard } from './components/ProjectionCard';
import { RecommendedTools } from './components/RecommendedTools';
import { SectionHeader } from './components/SectionHeader';
import { StudyCalendarSection } from './components/StudyCalendarSection';
import { StudyProfileCard } from './components/StudyProfileCard';
import { buildPlanSummary } from './utils/planSummary';

const STRINGS = appStrings.modules.satActBooster.results;

type SatActResultsPageProps = Readonly<{
  /** The study plan to render; the container resolves it before this page mounts. */
  plan: BoosterPlan;
  /** The report's test, used to label the plan summary; null when it can't be resolved. */
  test: TargetTest | null;
  /** When true, the plan is gated behind signup and rendered blurred. */
  blurred: boolean;
  onSignup: () => void;
  /** Optional secondary action on the signup gate; omitted where there's no way back. */
  onGoBack?: () => void;
}>;

/**
 * Results page: Plan at a Glance, the study-plan calendar, and recommended tools — each with a
 * Lubav action in its section header. Owns the logged-out blur gate.
 */
export const SatActResultsPage = ({
  plan,
  test,
  blurred,
  onSignup,
  onGoBack,
}: SatActResultsPageProps) => {
  const content = (
    <Box sx={contentSx} id="booster-results-content" aria-hidden={blurred}>
      <PlanAtAGlance summary={buildPlanSummary(test, plan)} stats={plan.stats} />

      {/* Projected score and study profile sit side by side; either falls back to full width. */}
      <Box sx={splitRowSx}>
        {plan.projection && <ProjectionCard projection={plan.projection} />}

        <StudyProfileCard phase={plan.phase} sections={plan.sections} />
      </Box>

      <Box sx={sectionSx}>
        <SectionHeader
          title={STRINGS.studyPlan.title}
          supporting={STRINGS.studyPlan.subtitle}
          action={
            <LubavButton
              id="booster-summarize-plan-btn"
              label={STRINGS.lubav.summarizeLabel}
              chatMessage={STRINGS.lubav.summarizeChatMessage}
              prompt={STRINGS.lubav.summarizePrompt}
            />
          }
        />
        {/* The computed flag from the plan response wins over the in-form estimate. */}
        {plan.insufficientSchedule?.flag && (
          <InsufficientScheduleCallout reasons={plan.insufficientSchedule.reasons} />
        )}
        <StudyCalendarSection plan={plan} />
      </Box>

      <Box sx={sectionSx}>
        <SectionHeader
          title={STRINGS.recommendedTools.title}
          supporting={STRINGS.recommendedTools.subtitle}
          action={
            <LubavButton
              id="booster-discuss-lubav-btn"
              label={STRINGS.lubav.discussLabel}
              chatMessage={STRINGS.lubav.discussChatMessage}
              prompt={STRINGS.lubav.discussPrompt}
            />
          }
        />
        <RecommendedTools />
      </Box>
    </Box>
  );

  if (!blurred) return content;

  return (
    <Box sx={gateContainerSx}>
      <InlineCtaCard
        title={STRINGS.gateTitle}
        supportingText={STRINGS.gateSupporting}
        primaryAction={{
          label: STRINGS.signupCta,
          onClick: onSignup,
          id: 'booster-plan-gate-signup-btn',
        }}
        secondaryAction={
          onGoBack
            ? { label: STRINGS.goBack, onClick: onGoBack, id: 'booster-plan-gate-go-back-btn' }
            : undefined
        }
      />
      <Box sx={blurredSx}>{content}</Box>
    </Box>
  );
};

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
});

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

// Two equal-width cards on desktop, stacked on mobile; a lone card takes the full width.
const splitRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: 'stretch',
  gap: backrs.spacing['spacing-3xl'],
  '& > *': {
    flex: { md: '1 1 0' },
    minWidth: 0,
  },
});

const gateContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const blurredSx: SxProps<Theme> = {
  filter: 'blur(6px)',
  pointerEvents: 'none',
  userSelect: 'none',
};
